import React, { useEffect, useState } from "react";

import { Alert, Box, Button, Stack } from "@mui/material";

import { ClipLoader } from "react-spinners";

import {
    getMicrosoftBingMappings,
    postMicrosoftBingMappings,
} from "helpers/destinations/microsoftBing";

import SwalAlertComp from "components/SwalAlert";

import MicrosoftBingMappingContent from "./MicrosoftBingMappingContent";

const MicrosoftBingMappingEdit = ({
    destinationId,
    onSaveSuccess,
    isModal = false,
}) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const [conversions, setConversions] = useState([]);
    const [events, setEvents] = useState([]);
    const [autoMatchedEvents, setAutoMatchedEvents] = useState([]);
    const [selectedMappings, setSelectedMappings] = useState([]);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

    useEffect(() => {
        fetchMappings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinationId]);

    const fetchMappings = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getMicrosoftBingMappings(destinationId);

            if (response.status && response.data) {
                const data = response.data;

                // Set conversions and events
                setConversions(data.conversions?.all_conversions || []);
                setEvents(data.conversions?.all_events || []);
                setAutoMatchedEvents(
                    data.conversions?.auto_matched_events || [],
                );

                // Set existing mappings or initialize with empty array
                if (data.mappings && data.mappings.length > 0) {
                    setSelectedMappings(
                        data.mappings.map((m) => ({
                            conversion_id: m.conversion_id?.toString(),
                            event: m.event?.toString(),
                            conversion_name: m.conversion_name || "",
                        })),
                    );
                } else {
                    // Initialize with one empty mapping
                    setSelectedMappings([
                        {
                            conversion_id: "",
                            event: "",
                            conversion_name: "",
                        },
                    ]);
                }
            } else {
                setError(response.message || "Failed to fetch mappings");
            }
        } catch (err) {
            console.error("Error fetching Microsoft Bing mappings:", err);
            setError("An error occurred while fetching mappings");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveMappings = async () => {
        setSaving(true);
        setError(null);
        setSuccessMessage(null);

        try {
            // Validate that all mappings have both conversion and event selected
            const hasInvalidMapping = selectedMappings.some(
                (mapping) => !mapping.conversion_id || !mapping.event,
            );

            if (hasInvalidMapping) {
                setError(
                    "Please select both conversion and event for all mappings",
                );
                setSaving(false);
                return;
            }

            const response = await postMicrosoftBingMappings(destinationId, {
                mappings: selectedMappings.map((m) => ({
                    conversion_id: m.conversion_id?.toString(),
                    event: m.event?.toString(),
                    conversion_name:
                        m.conversion_name ||
                        conversions?.find(
                            (c) =>
                                c.id?.toString() ===
                                m.conversion_id?.toString(),
                        )?.name ||
                        "",
                })),
            });

            if (response.status) {
                setSuccessMessage("Mappings saved successfully!");
                SwalAlertComp({
                    title: "Success",
                    message: "Mappings saved successfully!",
                    icon: "success",
                    timer: 3000,
                });
                // Call onSaveSuccess callback if provided after a brief delay to show success message
                if (onSaveSuccess) {
                    setTimeout(() => {
                        onSaveSuccess();
                    }, 1000);
                }
            } else {
                setError(response.message || "Failed to save mappings");
            }
        } catch (err) {
            console.error("Error saving Microsoft Bing mappings:", err);
            setError("An error occurred while saving mappings");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        setIsSubmitDisabled(
            selectedMappings.some(
                (mapping) => !mapping.conversion_id || !mapping.event,
            ),
        );
    }, [selectedMappings]);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
            >
                <ClipLoader size={24} color="#666" />
            </Box>
        );
    }

    return (
        <Stack spacing={2}>
            {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <MicrosoftBingMappingContent
                conversions={conversions}
                events={events}
                autoMatchedEvents={autoMatchedEvents}
                selectedMappings={selectedMappings}
                setSelectedMappings={setSelectedMappings}
            />

            <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveMappings}
                    disabled={saving || isSubmitDisabled}
                >
                    {saving ? (
                        <>
                            <ClipLoader size={16} color="#fff" />
                            <span style={{ marginLeft: "8px" }}>Saving...</span>
                        </>
                    ) : (
                        "Save Mappings"
                    )}
                </Button>
            </Box>
        </Stack>
    );
};

export default MicrosoftBingMappingEdit;
